import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { FeeAmountType, FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { UserComment } from '@core/model/comment.model'
import { ScopeVersion } from '@core/model/scope-version'
import { SecondParty } from '@core/model/second-party.model'
import { User } from '@core/model/user.model'
import { DeliverableMoneyUtils } from '@shared/utils/deliverable-money.utils'
import { isNonTradedDeliverable } from '@shared/utils/utils'
import { Type } from 'class-transformer'

export class ScopeSection {
  id!: number
  order!: number
  name!: string
  @Type(() => SecondParty) secondParty!: SecondParty
  @Type(() => Date) startDate!: Date
  @Type(() => Date) endDate!: Date
  @Type(() => Date) createdTs!: Date
  @Type(() => Date) updatedTs!: Date
  @Type(() => User) createdBy!: User
  @Type(() => User) updatedBy!: User
  @Type(() => Money) naturalSellingPrice!: Money
  @Type(() => Money) totalCostPrice!: Money
  totalProfit: number
  @Type(() => Money) totalSellingPrice!: Money
  @Type(() => Money) unitCostPrice!: Money
  @Type(() => Money) unitProfit!: Money
  @Type(() => Money) unitSellingPrice!: Money
  @Type(() => Deliverable) deliverables!: Deliverable[]
  @Type(() => ThirdPartyCost) thirdPartyCosts!: ThirdPartyCost[]
  currencyUnit!: string
  markup!: number
  @Type(() => FeeItemInstance) feeItems!: FeeItemInstance[]
  description!: string
  internalNote!: string
  @Type(() => UserComment) comments!: UserComment[]

  getNaturalSellingPrice(scope: ScopeVersion) {
    let total = new Money(0, this.currencyUnit)
    const deliverables = this.getDeliverables(scope)
    deliverables?.filter(isNonTradedDeliverable).forEach((d) => {
      const deliverablePrice = d.getTotalSellingPrice()
      total = DeliverableMoneyUtils.addAndNormalizeMoney(total, deliverablePrice)
    })
    return total
  }

  getFeePrice(fee: FeeItemInstance, amount: number): number {
    if (fee.feeItem.amountType === FeeAmountType.MONETARY) {
      return parseFloat(fee.feeItem.amount.toFixed(2))
    } else {
      return parseFloat(((amount * fee.feeItem.amount) / 100).toFixed(2))
    }
  }

  getTotalFees(scope: ScopeVersion, amount: number | null = null) {
    let currentTotal = 0
    const fees = this.feeItems
    if (fees != null && fees.length !== 0) {
      if (amount == null && this.feeItems.find((fee) => fee.feeItem?.amountType !== FeeAmountType.MONETARY)) {
        amount = this.getNaturalSellingPrice(scope)?.amount
      }
      for (let i = 0; i < this.feeItems.length; i++) {
        currentTotal += Number(this.getFeePrice(this.feeItems[i], amount))
      }
    }
    return parseFloat(currentTotal.toFixed(2))
  }

  getTotalSellingPrice(scope: ScopeVersion) {
    const naturalSellingPrice = this.getNaturalSellingPrice(scope)
    const calculatedPrice = naturalSellingPrice.addValue(this.getTotalFees(scope))
    if (calculatedPrice.amount === 0 && this.totalSellingPrice && this.totalSellingPrice.amount != null) {
      return this.totalSellingPrice
    }
    return calculatedPrice
  }

  getAgencyHours(scope: ScopeVersion) {
    let agencyHours = 0
    const deliverables = this.getDeliverables(scope)
    if (deliverables?.length > 0) {
      agencyHours = deliverables.map((d) => d.getTotalRateCardHours() || 0).reduce((o, n) => o + n, 0)
    }
    return agencyHours
  }

  scopemarkHours(scope: ScopeVersion) {
    let scopemarkHours = 0
    const deliverables = this.getDeliverables(scope)
    if (deliverables.length > 0) {
      scopemarkHours = deliverables.map((d) => d.scopeMarkTotalHours).reduce((o, n) => o + n, 0)
    }
    return scopemarkHours
  }

  getScopemarkPrice(scope: ScopeVersion) {
    const currency = scope.getRateCardCurrency()
    const deliverables = this.getDeliverables(scope)
    if (deliverables.length > 0) {
      let deliverableValues = deliverables.map((d) => d.scopeMarkTotalSellingPrice?.amount).reduce((o, n) => o + n, 0)
      deliverableValues += this.getTotalFees(scope)
      return new Money(deliverableValues, currency)
    }
    return new Money(0, currency)
  }

  getDeliverables = (scope: ScopeVersion) => {
    return this.deliverables || scope?.deliverables?.filter((d) => d.section?.id === this.id)
  }
}
